import { t, type TKey } from "@/lib/i18n";

/**
 * The field conditions of a visit (proto FieldMatch / CampaignRule FIELD):
 * the server's internal/domain/match.go is the authority — it normalises and
 * refuses what no runtime can serve. This is what the pickers offer.
 */
export const MATCH_FIELDS = ["isp", "os", "browser", "region", "city", "asn", "ip", "user_agent", "referrer", "language", "device_type", "country"] as const;
export type MatchFieldName = (typeof MATCH_FIELDS)[number];

export const MATCH_OPS = ["in", "not_in", "contains", "not_contains", "regex", "not_regex", "present", "not_present"] as const;
export type MatchOpName = (typeof MATCH_OPS)[number];

/** The names the edge's user-agent parser gives (internal/rule/useragent.go). */
export const OS_NAMES = ["iOS", "Android", "Windows", "macOS", "ChromeOS", "Linux"];
export const BROWSER_NAMES = ["Chrome", "Safari", "Facebook App", "Instagram", "TikTok", "Samsung Internet", "Firefox", "Edge", "Opera", "Yandex", "Headless Chrome"];

/** What the picker offers for a field's «one of» list. */
export function matchSuggestions(field: string): string[] {
  if (field === "os") return OS_NAMES;
  if (field === "browser") return BROWSER_NAMES;
  if (field === "device_type") return ["mobile", "tablet", "desktop"];
  return [];
}

export const isRegexOp = (op: string) => op === "regex" || op === "not_regex";

/** present / not_present: the visit carries the field (a link parameter
 * named with any value or none) — they take no values. */
export const isPresenceOp = (op: string) => op === "present" || op === "not_present";

/** The text a person typed as the op's values: one pattern per line for a
 * regex (a pattern may hold commas), else comma- or line-separated. Pure. */
export function parseMatchValues(op: string, text: string): string[] {
  const parts = isRegexOp(op) ? text.split(/\n+/) : text.split(/[,\n]+/);
  return [...new Set(parts.map((s) => s.trim()).filter(Boolean))];
}

/** The values back as the text field shows them. Pure. */
export function formatMatchValues(op: string, values: readonly string[]): string {
  return values.join(isRegexOp(op) ? "\n" : ", ");
}

export function matchFieldLabel(field: string): string {
  if (field.startsWith("param:")) return `${t("match.field.param")} ${field.slice(6)}`;
  return (MATCH_FIELDS as readonly string[]).includes(field) ? t(`match.field.${field}` as TKey) : field;
}

export function matchOpLabel(op: string): string {
  return (MATCH_OPS as readonly string[]).includes(op) ? t(`match.op.${op}` as TKey) : op;
}

export function matchValuesHint(field: string, op: string): string {
  if (isRegexOp(op)) return t("match.values.regex");
  if (field === "ip" && (op === "in" || op === "not_in")) return t("match.values.ip");
  return t("match.values.list");
}

/** A browser probe of a pattern: refuses what JavaScript cannot compile (the
 * server's bounds are stricter and decide). Null = fine. */
export function patternError(pattern: string): string | null {
  try {
    new RegExp(pattern, "i");
    return null;
  } catch (e) {
    return e instanceof Error ? e.message.replace(/^Invalid regular expression:\s*/, "") : "invalid pattern";
  }
}
